import { DropdownFieldWrapper } from "@app/shared/components/DropdownFieldWrapper/DropdownFieldWrapper";
import { getFlagByLocale, SupportedLocales } from "@app/shared/utils/locales";
import { Tooltip } from "@design-system/DataDisplay/Tooltip";
import { Icon } from "@design-system/Icon";
import { Textfield } from "@design-system/Inputs/Textfield";
import { Box } from "@design-system/Layout/Box";
import { FlexCol } from "@design-system/Layout/Flex";
import { Modal } from "@design-system/Modal";
import { Text } from "@design-system/Typography/Text";
import {
    useState,
    type ChangeEvent,
    type Dispatch,
    type FC,
    type PropsWithChildren,
    type SetStateAction,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCreateExternalQuestionnaire } from "../../data";
import { DateInputDropdown } from "../../DateInputDropdown";
import { keys, RESOURCE_NAME } from "../../i18n";
import {
    QuestionnaireTemplate,
    type QuestionnaireCreationModalStep,
} from "../../types";

import { useCurrentUserLocale } from "@app/shared/utils/currentUserLocale";
import { RESOURCE_NAME as LOCALES_RESOURCE_NAME } from "@app/shared/utils/locales";
import { Dropdown } from "@design-system/Inputs/UniversalDropdown";

export const QuestionnaireFormStep: FC<{
    template: QuestionnaireTemplate | undefined;
    onStepChange: Dispatch<SetStateAction<QuestionnaireCreationModalStep>>;
    onTemplateChange: Dispatch<
        SetStateAction<QuestionnaireTemplate | undefined>
    >;
}> = ({ template, onStepChange, onTemplateChange }) => {
    const { t } = useTranslation(RESOURCE_NAME);
    const { t: tLocales } = useTranslation(LOCALES_RESOURCE_NAME);
    const { createAsync, isPending } = useCreateExternalQuestionnaire();
    const navigate = useNavigate();

    const userLocale = useCurrentUserLocale(SupportedLocales.EN);

    const [name, setName] = useState<string | undefined>(
        template?.name ?? undefined,
    );
    const [email, setEmail] = useState<string | undefined>();
    const [companyName, setCompanyName] = useState<string | undefined>();
    const [deadline, setDeadline] = useState<Date | undefined>();
    const [defaultLocale, setDefaultLocale] = useState<string>(
        template?.locale?.availableLocales?.[0]
            ? template?.locale?.availableLocales?.[0]
            : userLocale,
    );

    const handleBack = () => {
        onTemplateChange(undefined);
        onStepChange("templates");
    };

    const handleLocaleChange = (locale: string[]) => {
        if (locale.length === 0) return;
        const newLocale = locale[0];
        setDefaultLocale(newLocale);
    };

    const makeEventHandler =
        (setter: Dispatch<SetStateAction<string | undefined>>) =>
        (e: ChangeEvent<HTMLInputElement>) => {
            setter(e.target.value);
        };

    const canConfirm = !!name && !!defaultLocale;
    const createQuestionnaire = async () => {
        if (!canConfirm) return;

        const { createQuestionnaire } = await createAsync({
            templateId: template?.id,
            input: {
                name,
                requesterEmail: email,
                companyName,
                deadline,
                locale: {
                    availableLocales: [defaultLocale],
                    default: defaultLocale,
                },
            },
        });

        navigate(`${createQuestionnaire.data?.id}`);
    };
    const isLocale = (value: string): value is SupportedLocales => {
        return Object.values(SupportedLocales).includes(
            value as SupportedLocales,
        );
    };
    const getLocalFlag = (locale: string) => {
        if (isLocale(locale)) {
            return getFlagByLocale(locale);
        }
        return getFlagByLocale(defaultLocale as SupportedLocales);
    };

    return (
        <Modal.Content className="max-w-[600px]">
            <Modal.Header withClose>
                <Modal.Title>
                    {t(keys.create_questionnaire_modal_title)}
                </Modal.Title>

                {template && (
                    <Modal.Subtitle>
                        {t(keys.create_questionnaire_modal_subtitle_templates)}
                    </Modal.Subtitle>
                )}
            </Modal.Header>
            <Modal.Body>
                <FlexCol gap="6">
                    <FlexCol gap="4">
                        <Textfield
                            className="h-6"
                            value={name}
                            onChange={makeEventHandler(setName)}
                            placeholder={t(
                                keys.create_questionnaire_modal_name_placeholder,
                            )}
                            required
                        />

                        <DateInputDropdown
                            value={deadline}
                            onChange={setDeadline}
                        />

                        {!template && (
                            <FlexCol gap="1">
                                <Dropdown
                                    value={[defaultLocale]}
                                    onValueChange={handleLocaleChange}
                                >
                                    <Dropdown.Trigger className="w-[204px]">
                                        <DropdownFieldWrapper
                                            placeholder={t(
                                                keys.create_questionnaire_modal_locale_placeholder,
                                            )}
                                            placeholderUp={!!defaultLocale}
                                            required
                                            leadingElement={
                                                <Icon
                                                    name={getLocalFlag(
                                                        defaultLocale,
                                                    )}
                                                />
                                            }
                                            trailingElement={
                                                <Icon name="chevronDown" />
                                            }
                                        >
                                            <Text>
                                                {tLocales(defaultLocale)}
                                            </Text>
                                        </DropdownFieldWrapper>
                                    </Dropdown.Trigger>
                                    <Dropdown.Content align="start" hasPortal>
                                        {Object.values(SupportedLocales).map(
                                            (locale) => (
                                                <Dropdown.SelectableItem
                                                    key={locale}
                                                    value={locale}
                                                >
                                                    <Dropdown.ItemIcon
                                                        name={getLocalFlag(
                                                            locale,
                                                        )}
                                                    />

                                                    <Dropdown.ItemText>
                                                        {tLocales(locale)}
                                                    </Dropdown.ItemText>
                                                    <Dropdown.SelectedIndicator />
                                                </Dropdown.SelectableItem>
                                            ),
                                        )}
                                    </Dropdown.Content>
                                </Dropdown>

                                <Text variant="body" color="secondary">
                                    {t(keys.locale_description)}
                                </Text>
                            </FlexCol>
                        )}
                    </FlexCol>
                    <FlexCol gap="2">
                        <FlexCol gap="1">
                            <Text variant="body-lg-bold" color="primary">
                                {t(keys.requested_by)}
                            </Text>
                            <Text variant="body" color="secondary">
                                {t(keys.requested_by_description)}
                            </Text>
                        </FlexCol>
                        <FlexCol gap="4">
                            <Textfield
                                className="h-6"
                                value={companyName}
                                onChange={makeEventHandler(setCompanyName)}
                                placeholder={t(
                                    keys.create_questionnaire_modal_company_name_placeholder,
                                )}
                            />
                            <Textfield
                                className="h-6"
                                value={email}
                                onChange={makeEventHandler(setEmail)}
                                placeholder={t(
                                    keys.create_questionnaire_modal_requester_email_placeholder,
                                )}
                            />
                        </FlexCol>
                    </FlexCol>
                </FlexCol>
            </Modal.Body>
            <Modal.Footer>
                <Modal.CancelCTA onClick={handleBack}>
                    {t(keys.create_questionnaire_modal_cancel_label)}
                </Modal.CancelCTA>
                <Tooltip>
                    <Tooltip.Trigger asChild>
                        {/* This Box below is necessary because the tooltip doesn't open when the button is
                         * disabled but we want to display the tooltip just when the button is disabled
                         */}
                        <Box className="w-fit self-end">
                            <Modal.ConfirmCTA
                                onClick={createQuestionnaire}
                                disabled={!canConfirm}
                                loading={isPending}
                                preventClose
                            >
                                {t(
                                    keys.create_questionnaire_modal_confirm_label,
                                )}
                            </Modal.ConfirmCTA>
                        </Box>
                    </Tooltip.Trigger>
                    {!canConfirm && (
                        <Tooltip.Content>
                            {t(keys.create_questionnaire_modal_tooltip_no_name)}
                        </Tooltip.Content>
                    )}
                </Tooltip>
            </Modal.Footer>
        </Modal.Content>
    );
};

export const QuestionnaireField: FC<PropsWithChildren<{ label: string }>> = ({
    children,
    label,
}) => {
    return (
        <FlexCol gap="1.5">
            <Text variant="body-bold" color="primary">
                {label}
            </Text>
            {children}
        </FlexCol>
    );
};
